import React, { useState, useEffect, useContext, useRef } from 'react';
import { Form, Button, Card, Alert, Row, Col, Tabs, Tab, ListGroup } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import AuthContext from '../../context/AuthContext';
import MaterialsSection from '../materials/MaterialsSection';

// Autocomplete component for client selection
const ClientAutocomplete = ({ clients, selectedClientId, onSelectClient, disabled }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [showDropdown, setShowDropdown] = useState(false);
  const [filteredClients, setFilteredClients] = useState([]);
  const dropdownRef = useRef(null);
  
  // Get selected client name
  const selectedClient = clients.find(c => c.ClientId === parseInt(selectedClientId));
  
  useEffect(() => {
    // If a client is selected, show their name in the input
    if (selectedClient) {
      setSearchTerm(selectedClient.Name);
    }
  }, [selectedClient]);
  
  useEffect(() => {
    // Filter clients based on search term
    if (searchTerm === '') {
      setFilteredClients([]);
      setShowDropdown(false);
    } else {
      const filtered = clients.filter(client =>
        client.Name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (client.Email && client.Email.toLowerCase().includes(searchTerm.toLowerCase()))
      );
      setFilteredClients(filtered);
      setShowDropdown(filtered.length > 0);
    }
  }, [searchTerm, clients]);
  
  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  
  const handleInputChange = (e) => {
    setSearchTerm(e.target.value);
    // Clear selection when user starts typing
    if (selectedClient && e.target.value !== selectedClient.Name) {
      onSelectClient('');
    }
  };
  
  const handleSelectClient = (client) => {
    setSearchTerm(client.Name);
    onSelectClient(client.ClientId);
    setShowDropdown(false);
  };
  
  const handleInputFocus = () => {
    // Show all clients when input is focused and empty
    if (searchTerm === '') {
      setFilteredClients(clients);
      setShowDropdown(true);
    }
  };
  
  return (
    <div ref={dropdownRef} style={{ position: 'relative' }}>
      <Form.Group className="mb-3">
        <Form.Label>Search for Client</Form.Label>
        <Form.Control
          type="text"
          value={searchTerm}
          onChange={handleInputChange}
          onFocus={handleInputFocus}
          disabled={disabled}
          placeholder="Start typing client name or email..."
          autoComplete="off"
        />
        <Form.Text className="text-muted">
          Type to search existing clients by name or email
        </Form.Text>
      </Form.Group>
      
      {showDropdown && !disabled && (
        <div
          style={{
            position: 'absolute',
            top: '100%',
            left: 0,
            right: 0,
            maxHeight: '300px',
            overflowY: 'auto',
            backgroundColor: 'white',
            border: '1px solid #dee2e6',
            borderRadius: '0.25rem',
            boxShadow: '0 0.5rem 1rem rgba(0, 0, 0, 0.15)',
            zIndex: 1000
          }}
        >
          <ListGroup variant="flush">
            {filteredClients.map(client => (
              <ListGroup.Item
                key={client.ClientId}
                action
                onClick={() => handleSelectClient(client)}
                style={{
                  cursor: 'pointer',
                  backgroundColor: selectedClientId === client.ClientId ? '#e7f3ff' : 'white'
                }}
              >
                <div className="fw-bold">{client.Name}</div>
                {client.Email && <small className="text-muted">{client.Email}</small>}
                {client.Phone && <small className="text-muted"> • {client.Phone}</small>}
              </ListGroup.Item>
            ))}
          </ListGroup>
        </div>
      )}
    </div>
  );
};

const CreateProject = () => {
  const [formData, setFormData] = useState({
    projectName: '',
    clientId: '',
    startDate: '',
    endDate: '',
    generalRequirement: ''
  });
  
  // Cost items state
  const [costItems, setCostItems] = useState([]);
  const [costItemForm, setCostItemForm] = useState({ description: '', amount: '' });
  
  const [hasGeneralRequirement, setHasGeneralRequirement] = useState(false);
  
  const [newClientData, setNewClientData] = useState({
    name: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    state: '',
    zipCode: ''
  });
  
  // Initialize scope with bullet point
  const [scopeData, setScopeData] = useState({
    scopeName: '',
    scopeInformation: '• '
  });
  
  const [scopes, setScopes] = useState([]);
  const [clients, setClients] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [activeTab, setActiveTab] = useState('select');
  
  // Materials state
  const [materials, setMaterials] = useState([]);
  const [currentMaterial, setCurrentMaterial] = useState({
    materialName: '',
    category: '',
    quantity: '',
    unit: 'each',
    unitCost: '',
    vendor: '',
    purchaseDate: '',
    notes: ''
  });
  const [categories, setCategories] = useState([]);
  const [vendors, setVendors] = useState([]);
  
  const { token } = useContext(AuthContext);
  const navigate = useNavigate();

  // Fetch clients for dropdown
  useEffect(() => {
    const fetchClients = async () => {
      try {
        const response = await fetch('/api/customers/clients/all', {
          headers: {
            'x-auth-token': token
          }
        });
        
        if (!response.ok) {
          throw new Error('Failed to fetch clients');
        }
        
        const data = await response.json();
        setClients(data);
      } catch (err) {
        console.error('Error fetching clients:', err);
        setError('Failed to load clients. Please try again later.');
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchClients();
  }, [token]);
  
  const { projectName, clientId, startDate, endDate, generalRequirement } = formData;
  
  // Calculate sum of cost items
  const costItemsTotal = costItems.reduce((sum, item) => {
    const amt = parseFloat((item.amount || '0').toString().replace(/,/g, '')) || 0;
    return sum + amt;
  }, 0);
  
  // Calculate total cost for display
  const calculateTotalCost = () => {
    const general = parseFloat((generalRequirement || '0').replace(/,/g, '') || 0);
    return costItemsTotal + general;
  };
  
  const totalCost = calculateTotalCost();
  const { name, email, phone, address, city, state, zipCode } = newClientData;
  const { scopeName, scopeInformation } = scopeData;
  
  const onProjectChange = e => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };
  
  // Format currency input
  const formatCurrency = (value) => {
    // Remove any non-digit characters
    const numericValue = value.replace(/\D/g, '');
    
    // Format with commas
    if (numericValue === '') return '';
    
    // Convert to number and format with commas
    const formattedValue = new Intl.NumberFormat('en-US', {
      style: 'decimal',
      maximumFractionDigits: 0
    }).format(parseInt(numericValue, 10));
    
    return formattedValue;
  };
  
  // Cost item handlers
  const handleCostItemFormChange = (e) => {
    const { name, value } = e.target;
    if (name === 'amount') {
      setCostItemForm({ ...costItemForm, amount: formatCurrency(value) });
    } else {
      setCostItemForm({ ...costItemForm, [name]: value });
    }
  };
  
  const addCostItem = () => {
    if (!costItemForm.description || !costItemForm.amount) {
      setError('Cost item description and amount are required');
      return;
    }
    const newItem = {
      id: Date.now(),
      description: costItemForm.description,
      amount: costItemForm.amount
    };
    setCostItems([...costItems, newItem]);
    setCostItemForm({ description: '', amount: '' });
    setError('');
  };
  
  const removeCostItem = (itemId) => {
    setCostItems(costItems.filter(item => item.id !== itemId));
  };
  
  const handleGeneralRequirementChange = (e) => {
    const formattedValue = formatCurrency(e.target.value);
    setFormData({ ...formData, generalRequirement: formattedValue });
  };
  
  const onClientChange = e => {
    setNewClientData({ ...newClientData, [e.target.name]: e.target.value });
  };
  
  const onScopeChange = e => {
    setScopeData({ ...scopeData, [e.target.name]: e.target.value });
  };
  
  // Handle keydown in scope information field to add bullet points
  const handleScopeInfoKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault(); // Prevent default enter behavior
      
      const cursorPosition = e.target.selectionStart;
      const currentValue = e.target.value;
      
      // Insert new line with bullet point at cursor position
      const newValue = 
        currentValue.substring(0, cursorPosition) + 
        '\n• ' + 
        currentValue.substring(cursorPosition);
      
      setScopeData({
        ...scopeData,
        scopeInformation: newValue
      });
      
      // Schedule setting the cursor position after React updates the DOM
      setTimeout(() => {
        if (e.target) {
          e.target.selectionStart = cursorPosition + 3; // Position after the bullet point
          e.target.selectionEnd = cursorPosition + 3;
        }
      }, 0);
    }
  };
  
  const addScope = e => {
    e.preventDefault();
    
    if (!scopeName || !scopeInformation) {
      setError('Both Scope and Scope Details are required');
      return;
    }
    
    // Add the new scope to the scopes array
    const newScope = {
      id: Date.now(), // Use timestamp as temporary ID
      scopeName,
      scopeInformation
    };
    
    setScopes([...scopes, newScope]);
    
    // Reset the scope form
    setScopeData({
      scopeName: '',
      scopeInformation: '• '
    });
    
    toast.success('Scope added');
  };
  
  const removeScope = (scopeId) => {
    setScopes(scopes.filter(scope => scope.id !== scopeId));
    toast.success('Scope removed');
  };
  
  // Material handlers
  const onMaterialChange = e => {
    const { name, value } = e.target;
    setCurrentMaterial({ ...currentMaterial, [name]: value });
  };
  
  const addMaterial = () => {
    const { materialName, quantity, unitCost } = currentMaterial;
    
    if (!materialName || !quantity || !unitCost) {
      setError('Material name, quantity, and unit cost are required');
      return;
    }
    
    // Calculate total cost
    const totalCost = parseFloat(quantity) * parseFloat(unitCost);
    
    // Add the new material to the materials array
    const newMaterial = {
      id: Date.now(),
      ...currentMaterial,
      totalCost: totalCost.toFixed(2)
    };
    
    setMaterials([...materials, newMaterial]);
    
    // Add category and vendor to autocomplete lists if they're new
    if (currentMaterial.category && !categories.includes(currentMaterial.category)) {
      setCategories([...categories, currentMaterial.category]);
    }
    if (currentMaterial.vendor && !vendors.includes(currentMaterial.vendor)) {
      setVendors([...vendors, currentMaterial.vendor]);
    }
    
    // Reset the material form
    setCurrentMaterial({
      materialName: '',
      category: '',
      quantity: '',
      unit: 'each',
      unitCost: '',
      vendor: '',
      purchaseDate: '',
      notes: ''
    });
    
    toast.success('Material added');
  };
  
  const removeMaterial = (materialId) => {
    setMaterials(materials.filter(material => material.id !== materialId));
    toast.success('Material removed');
  };
  
  const createNewClient = async () => {
    // Validate required fields
    if (!name || !email) {
      setError('Client name and email are required');
      return null;
    }
    
    try {
      const response = await fetch('/api/customers/clients', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-auth-token': token
        },
        body: JSON.stringify(newClientData)
      });
      
      if (!response.ok) {
        throw new Error('Failed to create client');
      }
      
      const data = await response.json();
      toast.success('Client created successfully!');
      
      // Add the new client to the clients list
      setClients([...clients, data]);
      
      // Return the new client ID
      return data.ClientId;
    } catch (err) {
      console.error('Error creating client:', err);
      setError('Failed to create client: ' + (err.message || ''));
      return null;
    }
  };
  
  const createProjectScopes = async (projectId) => {
    // If there are no scopes, just return
    if (scopes.length === 0) return;
    
    try {
      // Create all scopes for the project
      for (const scope of scopes) {
        await fetch(`/api/projects/${projectId}/scopes`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'x-auth-token': token
          },
          body: JSON.stringify({
            scopeName: scope.scopeName,
            scopeInformation: scope.scopeInformation
          })
        });
      }
      
      console.log(`Created ${scopes.length} scopes for project ${projectId}`);
    } catch (err) {
      console.error('Error creating scopes:', err);
      // Don't throw error here, as we want to continue even if scopes fail
      toast.warning('Project created, but there was an issue adding some scopes');
    }
  };
  
  const createProjectMaterials = async (projectId) => {
    // If there are no materials, just return
    if (materials.length === 0) return;
    
    try {
      // Create all materials for the project
      for (const material of materials) {
        await fetch('/api/materials', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'x-auth-token': token
          },
          body: JSON.stringify({
            projectId: projectId,
            materialName: material.materialName,
            category: material.category,
            quantity: material.quantity,
            unit: material.unit,
            unitCost: material.unitCost,
            vendor: material.vendor,
            purchaseDate: material.purchaseDate || null,
            notes: material.notes
          })
        });
      }
      
      console.log(`Created ${materials.length} materials for project ${projectId}`);
    } catch (err) {
      console.error('Error creating materials:', err);
      // Don't throw error here, as we want to continue even if materials fail
      toast.warning('Project created, but there was an issue adding some materials');
    }
  };
  
  const createProjectCostItems = async (projectId) => {
    if (costItems.length === 0) return;
    
    try {
      for (const item of costItems) {
        await fetch(`/api/projects/${projectId}/cost-items`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'x-auth-token': token
          },
          body: JSON.stringify({
            description: item.description,
            amount: item.amount.toString().replace(/,/g, '')
          })
        });
      }
      console.log(`Created ${costItems.length} cost items for project ${projectId}`);
    } catch (err) {
      console.error('Error creating cost items:', err);
      toast.warning('Project created, but there was an issue adding some cost items');
    }
  };
  
  const onSubmit = async e => {
    e.preventDefault();
    setError('');
    setSubmitting(true);
    
    try {
      let selectedClientId = clientId;
      
      // If we're creating a new client, do that first
      if (activeTab === 'create') {
        const newClientId = await createNewClient();
        if (!newClientId) {
          setSubmitting(false);
          return; // Stop if client creation failed
        }
        selectedClientId = newClientId;
      } else if (!selectedClientId) {
        setError('Please select a client or create a new one');
        setSubmitting(false);
        return;
      }
      
      // Now create the project with the selected or new client ID
      if (!projectName) {
        setError('Project name is required');
        setSubmitting(false);
        return;
      }
      
      const response = await fetch('/api/projects', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-auth-token': token
        },
        body: JSON.stringify({
          projectName,
          clientId: selectedClientId,
          description: "Project details",
          startDate: startDate || null,
          endDate: endDate || null,
          sumOfTimeAndMaterials: costItemsTotal.toString(),
          generalRequirement: generalRequirement || "0"
        })
      });
      
      if (!response.ok) {
        throw new Error('Failed to create project');
      }
      
      const data = await response.json();
      
      // Create cost items for the project
      await createProjectCostItems(data.ProjectId);
      
      // Create scopes for the project
      await createProjectScopes(data.ProjectId);
      
      // Create materials for the project
      await createProjectMaterials(data.ProjectId);
      
      toast.success('Project created successfully!');
      navigate(`/projects/${data.ProjectId}`);
    } catch (err) {
      console.error('Error in project creation process:', err);
      setError(err.message || 'Failed to create project');
    } finally {
      setSubmitting(false);
    }
  };
  
  if (isLoading) {
    return <div className="text-center mt-5">Loading...</div>;
  }
  
  return (
    <div className="form-container">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2>Create New Project</h2>
      </div>
      
      <Card className="mb-4">
        <Card.Body>
          {error && <Alert variant="danger">{error}</Alert>}
          
          <Form onSubmit={onSubmit}>
            <h4>Project Information</h4>
            <Form.Group className="mb-3">
              <Form.Label>Project Name</Form.Label>
              <Form.Control
                type="text"
                name="projectName"
                value={projectName}
                onChange={onProjectChange}
                required
                placeholder="Enter project name"
              />
            </Form.Group>
            
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Start Date</Form.Label>
                  <Form.Control
                    type="date"
                    name="startDate"
                    value={startDate}
                    onChange={onProjectChange}
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>End Date</Form.Label>
                  <Form.Control
                    type="date"
                    name="endDate"
                    value={endDate}
                    onChange={onProjectChange}
                  />
                </Form.Group>
              </Col>
            </Row>
            
            <hr className="my-4" />
            
            <h4>Client Information</h4>
            <Tabs
              activeKey={activeTab}
              onSelect={(k) => setActiveTab(k)}
              className="mb-3"
            >
              <Tab eventKey="select" title="Select Existing Client">
                {clients.length > 0 ? (
                  <ClientAutocomplete
                    clients={clients}
                    selectedClientId={clientId}
                    onSelectClient={(id) => setFormData({ ...formData, clientId: id })}
                    disabled={activeTab !== 'select'}
                  />
                ) : (
                  <Alert variant="info">
                    No existing clients found. Please create a new client.
                  </Alert>
                )}
              </Tab>
              <Tab eventKey="create" title="Create New Client">
                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Client Name</Form.Label>
                      <Form.Control
                        type="text"
                        name="name"
                        value={name}
                        onChange={onClientChange}
                        required={activeTab === 'create'}
                        disabled={activeTab !== 'create'}
                        placeholder="Enter client name"
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Email</Form.Label>
                      <Form.Control
                        type="email"
                        name="email"
                        value={email}
                        onChange={onClientChange}
                        required={activeTab === 'create'}
                        disabled={activeTab !== 'create'}
                        placeholder="Enter client email"
                      />
                    </Form.Group>
                  </Col>
                </Row>
                
                <Form.Group className="mb-3">
                  <Form.Label>Phone</Form.Label>
                  <Form.Control
                    type="text"
                    name="phone"
                    value={phone}
                    onChange={onClientChange}
                    disabled={activeTab !== 'create'}
                    placeholder="Enter client phone number"
                  />
                </Form.Group>
                
                <Form.Group className="mb-3">
                  <Form.Label>Address</Form.Label>
                  <Form.Control
                    type="text"
                    name="address"
                    value={address}
                    onChange={onClientChange}
                    disabled={activeTab !== 'create'}
                    placeholder="Enter client address"
                  />
                </Form.Group>
                
                <Row>
                  <Col md={5}>
                    <Form.Group className="mb-3">
                      <Form.Label>City</Form.Label>
                      <Form.Control
                        type="text"
                        name="city"
                        value={city}
                        onChange={onClientChange}
                        disabled={activeTab !== 'create'}
                        placeholder="Enter city"
                      />
                    </Form.Group>
                  </Col>
                  <Col md={3}>
                    <Form.Group className="mb-3">
                      <Form.Label>State</Form.Label>
                      <Form.Control
                        type="text"
                        name="state"
                        value={state}
                        onChange={onClientChange}
                        disabled={activeTab !== 'create'}
                        placeholder="Enter state"
                      />
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <Form.Group className="mb-3">
                      <Form.Label>Zip Code</Form.Label>
                      <Form.Control
                        type="text"
                        name="zipCode"
                        value={zipCode}
                        onChange={onClientChange}
                        disabled={activeTab !== 'create'}
                        placeholder="Enter zip code"
                      />
                    </Form.Group>
                  </Col>
                </Row>
              </Tab>
            </Tabs>
            
            <hr className="my-4" />
            
            <h4>Project Scopes</h4>
            
            {/* Scope List */}
            {scopes.length > 0 && (
              <div className="mb-4">
                <ListGroup>
                  {scopes.map((scope, index) => (
                    <ListGroup.Item key={scope.id} className="d-flex justify-content-between align-items-start">
                      <div className="ms-2 me-auto">
                        <div className="fw-bold">{scope.scopeName}</div>
                        <div style={{ whiteSpace: 'pre-wrap' }}>{scope.scopeInformation}</div>
                      </div>
                      <Button 
                        variant="outline-danger" 
                        size="sm"
                        onClick={() => removeScope(scope.id)}
                      >
                        Remove
                      </Button>
                    </ListGroup.Item>
                  ))}
                </ListGroup>
              </div>
            )}
            
            {/* Add Scope Form */}
            <Card className="mb-4">
              <Card.Body>
                <Form.Group className="mb-3">
                  <Form.Label>Scope</Form.Label>
                  <Form.Control
                    type="text"
                    name="scopeName"
                    value={scopeName}
                    onChange={onScopeChange}
                    placeholder="Enter scope title (e.g., Painting, Electrical, Plumbing)"
                  />
                </Form.Group>
                
                <Form.Group className="mb-3">
                  <Form.Label>Scope Details</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={10}
                    name="scopeInformation"
                    value={scopeInformation}
                    onChange={onScopeChange}
                    onKeyDown={handleScopeInfoKeyDown}
                    onFocus={(e) => {
                      // Position cursor at the end (after bullet point and space)
                      const len = e.target.value.length;
                      setTimeout(() => {
                        e.target.selectionStart = len;
                        e.target.selectionEnd = len;
                      }, 0);
                    }}
                    placeholder="Press Enter for new bullet points"
                  />
                  <Form.Text className="text-muted">
                    Press Enter to add a new bullet point automatically. Bullet points improve readability in proposals.
                  </Form.Text>
                </Form.Group>
                
                <div className="d-flex justify-content-end">
                  <Button 
                    variant="success" 
                    onClick={addScope}
                  >
                    Add Scope
                  </Button>
                </div>
              </Card.Body>
            </Card>
            
            <hr className="my-4" />
            
            {/* Materials Section */}
            <MaterialsSection
              materials={materials}
              currentMaterial={currentMaterial}
              categories={categories}
              vendors={vendors}
              onMaterialChange={onMaterialChange}
              onAddMaterial={addMaterial}
              onRemoveMaterial={removeMaterial}
            />
            
            <hr className="my-4" />
            
            <h4>Project Costs</h4>
            <p className="text-muted mb-3">Add individual cost items for this project. These will be totaled as the Sum of Time and Materials.</p>
            
            {/* Cost Items List */}
            {costItems.length > 0 && (
              <div className="mb-3">
                <ListGroup>
                  {costItems.map((item) => (
                    <ListGroup.Item key={item.id} className="d-flex justify-content-between align-items-center">
                      <div>
                        <span className="fw-bold">{item.description}</span>
                      </div>
                      <div className="d-flex align-items-center">
                        <span className="me-3">${item.amount}</span>
                        <Button 
                          variant="outline-danger" 
                          size="sm"
                          onClick={() => removeCostItem(item.id)}
                        >
                          Remove
                        </Button>
                      </div>
                    </ListGroup.Item>
                  ))}
                </ListGroup>
              </div>
            )}
            
            {/* Add Cost Item Form */}
            <Card className="mb-4">
              <Card.Body>
                <Row>
                  <Col md={7}>
                    <Form.Group className="mb-2">
                      <Form.Label>Description</Form.Label>
                      <Form.Control
                        type="text"
                        name="description"
                        value={costItemForm.description}
                        onChange={handleCostItemFormChange}
                        placeholder="e.g., Cabinets, Trim, Interior Paint"
                      />
                    </Form.Group>
                  </Col>
                  <Col md={3}>
                    <Form.Group className="mb-2">
                      <Form.Label>Amount ($)</Form.Label>
                      <Form.Control
                        type="text"
                        name="amount"
                        value={costItemForm.amount}
                        onChange={handleCostItemFormChange}
                        placeholder="e.g., 8,000"
                      />
                    </Form.Group>
                  </Col>
                  <Col md={2} className="d-flex align-items-end mb-2">
                    <Button 
                      variant="success" 
                      onClick={addCostItem}
                      className="w-100"
                    >
                      Add
                    </Button>
                  </Col>
                </Row>
              </Card.Body>
            </Card>
            
            <Form.Group className="mb-3">
              <Form.Check
                type="checkbox"
                id="hasGeneralRequirement"
                label="Include General Requirement Pricing?"
                checked={hasGeneralRequirement}
                onChange={(e) => {
                  setHasGeneralRequirement(e.target.checked);
                  if (!e.target.checked) {
                    setFormData({ ...formData, generalRequirement: '' });
                  }
                }}
              />
            </Form.Group>
            
            {hasGeneralRequirement && (
              <Form.Group className="mb-4">
                <Form.Label>General Requirement ($)</Form.Label>
                <Form.Control
                  type="text"
                  name="generalRequirement"
                  value={generalRequirement}
                  onChange={handleGeneralRequirementChange}
                  placeholder="Enter general requirement amount (e.g., 1500)"
                />
                <Form.Text className="text-muted">
                  Enter the amount without $ sign or decimals. Commas will be added automatically.
                </Form.Text>
              </Form.Group>
            )}
            
            <div className="alert alert-info">
              <div className="d-flex justify-content-between align-items-center mb-1">
                <span>Sum of Time and Materials:</span>
                <span className="fw-bold">${costItemsTotal.toLocaleString()}</span>
              </div>
              {hasGeneralRequirement && parseFloat((generalRequirement || '0').replace(/,/g, '')) > 0 && (
                <div className="d-flex justify-content-between align-items-center mb-1">
                  <span>General Requirement:</span>
                  <span className="fw-bold">${parseFloat((generalRequirement || '0').replace(/,/g, '')).toLocaleString()}</span>
                </div>
              )}
              <hr className="my-2" />
              <div className="d-flex justify-content-between align-items-center">
                <h5 className="mb-0">Total Project Cost:</h5>
                <h4 className="mb-0 text-primary">
                  ${totalCost.toLocaleString()}
                </h4>
              </div>
            </div>
            
            <div className="d-flex justify-content-between mt-4">
              <Button 
                variant="secondary" 
                onClick={() => navigate('/projects')}
              >
                Cancel
              </Button>
              <Button 
                type="submit" 
                variant="primary"
                disabled={
                  submitting || 
                  (activeTab === 'select' && clients.length === 0)
                }
              >
                {submitting ? 'Creating...' : 'Create Project'}
              </Button>
            </div>
          </Form>
        </Card.Body>
      </Card>
    </div>
  );
};

export default CreateProject;
